'use client';
import { useTranslations } from 'next-intl';
import { type FC, useState } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { cn, reportEvent } from '@/app/utils';
import { tasksCancel } from '../apis';

interface CancelButtonProps {
  className?: string;
  taskId: string;
  onCancel?: (id: string) => void;
}

export const CancelButton: FC<CancelButtonProps> = ({ className, taskId, onCancel }) => {
  const t = useTranslations();

  // 避免重复点击（state 的更新由 SSE 负责，可能取消成功后 state 还未及时更新，此时隐藏按钮）
  const [isCanceled, setIsCanceled] = useState(false);

  const { loading, run } = useFetch(
    () =>
      tasksCancel({
        path: {
          id: taskId,
        },
        body: {
          id: taskId,
        },
      }),
    {
      manual: true,
      onSuccess: ({ error }) => {
        if (error) {
          if (error.reason === 'TaskCannotBeCanceled') {
            toast({
              type: 'warning',
              message: t('popup.toast.cancelFailedDueToProcessing'),
            });
            reportEvent('task_cancel_button_click', {
              task_id: taskId,
              is_success: false,
              fail_reason: error.reason,
            });
          } else {
            throw new Error(error.reason);
          }
          return;
        }
        toast({
          type: 'success',
          message: t('popup.toast.cancelSuccess'),
          duration: 3000,
        });
        onCancel?.(taskId);
        setIsCanceled(true);
        reportEvent('task_cancel_button_click', {
          task_id: taskId,
          is_success: true,
        });
      },
      onError: () => {
        toast({
          type: 'warning',
          message: t('popup.toast.cancelFailedOther'),
        });
        reportEvent('task_cancel_button_click', {
          task_id: taskId,
          is_success: false,
          fail_reason: 'other',
        });
      },
    },
  );

  const handleCancel = () => {
    if (loading) return;
    run();
  };

  if (isCanceled) return null;

  return (
    <Button
      className={cn('flex h-8 items-center rounded-8 px-3 font-normal', className)}
      onClick={handleCancel}
      disabled={loading}
    >
      {loading && <Loading className="mr-2 h-4 w-4 animate-spin" />}
      {t('task.action.cancel')}
    </Button>
  );
};
